/**
 * Map an existing WhatsApp template's placeholders to work-order fields (#4106).
 * Approved templates cannot be edited at Meta, so the mapping is a local PATCH.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogContent, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { useNotify } from '@/hooks/useNotify';
import { describeApiError } from '@/lib/describeApiError';
import {
  WHATSAPP_TEMPLATE_PARAMETER_SOURCES,
  isWhatsAppTemplateParameterSource,
  type WhatsAppTemplateParameterSource,
} from '@/lib/whatsappTemplateSources';
import type { WhatsAppAdminTemplate } from '@/hooks/useWhatsAppSettings';
import { useUpdateWhatsAppTemplateParameterSources } from './useWhatsAppTemplateMutations';

export type WhatsAppTemplateSourceMapping = WhatsAppAdminTemplate & {
  parameterSources?: readonly (WhatsAppTemplateParameterSource | null)[] | null;
};

function sourceAt(
  sources: readonly (WhatsAppTemplateParameterSource | null)[] | null | undefined,
  index: number,
): WhatsAppTemplateParameterSource | null {
  const value = sources?.[index];
  return isWhatsAppTemplateParameterSource(value) ? value : null;
}

interface WhatsAppTemplateSourcesDialogProps {
  template: WhatsAppTemplateSourceMapping | null;
  onClose: () => void;
}

export function WhatsAppTemplateSourcesDialog({
  template,
  onClose,
}: WhatsAppTemplateSourcesDialogProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const updateSources = useUpdateWhatsAppTemplateParameterSources();
  const [sources, setSources] = useState<(WhatsAppTemplateParameterSource | null)[]>([]);

  useEffect(() => {
    if (!template) return;
    setSources(
      Array.from({ length: template.parameterCount }, (_, index) =>
        sourceAt(template.parameterSources, index),
      ),
    );
  }, [template]);

  const handleSubmit = async () => {
    if (!template || template.parameterCount === 0) return;
    const parameterSources = Array.from(
      { length: template.parameterCount },
      (_, index) => sources[index] ?? null,
    );
    try {
      await updateSources.mutateAsync({ id: template.id, parameterSources });
      notify.success(t('whatsapp.parameterSourcesSaved'), t('whatsapp.parameterSourcesSavedDesc'));
      onClose();
    } catch (error) {
      notify.error(
        tc('error'),
        describeApiError(error, tc, { fallback: t('whatsapp.parameterSourcesSaveFailed') }),
      );
    }
  };

  const count = template?.parameterCount ?? 0;

  return (
    <FormDialog
      open={template !== null}
      onClose={onClose}
      title={t('whatsapp.editParameterSourcesTitle')}
      isDirty={sources.some((value, index) => value !== sourceAt(template?.parameterSources, index))}
      onSubmit={() => void handleSubmit()}
      isLoading={updateSources.isPending}
      submitDisabled={count === 0}
      maxWidth="xs"
    >
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {count === 0 ? t('whatsapp.noParameterFields') : t('whatsapp.parameterSourcesHint')}
          </Typography>
          {template &&
            Array.from({ length: count }, (_, index) => (
              <TextField
                key={`${template.id}-${String(index)}`}
                select
                label={t('whatsapp.parameterField', { index: index + 1 })}
                value={sources[index] ?? ''}
                onChange={(event) => {
                  const value = event.target.value;
                  setSources((prev) => {
                    const next = Array.from({ length: count }, (_, item) => prev[item] ?? null);
                    next[index] = isWhatsAppTemplateParameterSource(value) ? value : null;
                    return next;
                  });
                }}
                fullWidth
              >
                <MenuItem value="">{t('whatsapp.parameterSourceFreeText')}</MenuItem>
                {WHATSAPP_TEMPLATE_PARAMETER_SOURCES.map((key) => (
                  <MenuItem key={key} value={key}>
                    {t(`whatsapp.parameterSource.${key}`)}
                  </MenuItem>
                ))}
              </TextField>
            ))}
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
